// Fonte única do status real de um boleto. Usada pelo Painel Financeiro
// Executivo (views/Financeiro/PainelExecutivo) e pela Central Executiva de
// Inadimplência (views/CRM/Inadimplencia) — as duas telas precisam chegar ao
// MESMO número de inadimplência, e antes disso cada uma tinha a sua regra.
//
// Por que a regra é "campo OU data" para pagamento, mas "só campo" para
// cancelamento: a base de produção está dessincronizada, e cada data tem um
// grau de confiança diferente. Medido em 13/08/2026:
//
//   - `data_pgto_boleto` e `quitado_em` são CONFIÁVEIS: zero casos em que
//     aparecem contradizendo o status. Servem como evidência extra, e são
//     necessários no sentido inverso — 197 boletos têm status='Pago' SEM a
//     data (a confirmação manual de pagamento só grava o status), e derivar
//     só por data chamava esses 197 de vencidos.
//
//   - ⚠️ `cancelado_em` NÃO é confiável e por isso NÃO entra na regra.
//     Ele é carimbado quando um boleto é cancelado e **nunca é limpo quando
//     a fatura recebe um boleto novo** — e cancelar-para-reemitir é rotina
//     (os motivos mais comuns no log são "Atualizar o boleto" e "Atualizar a
//     data de vencimento a pedido do cliente"). Prova: 447 faturas têm
//     `cancelado_em` e status diferente de 'Cancelado'; dessas, 297 foram
//     PAGAS depois da data de cancelamento (R$ 454.754,29). Boleto cancelado
//     não é pago — logo o carimbo é do boleto anterior, não desta fatura.
//     Tratá-lo como prova escondia R$ 468 mil de recebido dentro de
//     "Cancelado" e sumia com R$ 53 mil de dívida real da inadimplência.
//
// Ou seja: `status_boleto_vigor` é a autoridade sobre o boleto ATUAL; as datas
// só corroboram, nunca contradizem. A data de vencimento decide o par
// Vencido/Em aberto — o único que muda sozinho com o passar do tempo e por
// isso não pode depender de um campo gravado no passado.
//
// Espelha a rotina operacional de FinanceiroLayout.tsx:1351-1380, que é o que
// de fato corrige `status_boleto_vigor` no banco: ela também só age sobre
// faturas com boleto calculado e também nunca sobrescreve Pago/Cancelado/
// Parcelado/Quitado.
//
// Função pura, sem I/O nem JSX: roda sob `node --test` com type-stripping.

export type StatusBoleto =
  | 'Pago'
  | 'Quitado'
  | 'Cancelado'
  | 'Parcelado'
  | 'Vencido'
  | 'Em aberto'
  | 'Sem cobrança'
  | 'Sem injeção'
  | 'Cobrança agrupada';

/** Status do pipeline de faturas em que já existe boleto. Antes disso a fatura
 *  ainda não virou cobrança — não pode entrar em inadimplência nem em emitido.
 *  Mesma lista de `isBoletoCalculado` em FinanceiroLayout.tsx:907. */
export const STATUS_FATURA_COM_BOLETO = [
  'Com boleto calculado',
  'Com boleto emitido / A enviar',
  'Cobrança enviada',
] as const;

/** Status derivados que não representam cobrança viva: ficam fora de todos os
 *  denominadores (emitido, taxa de inadimplência, ticket médio). */
export const STATUS_SEM_COBRANCA: StatusBoleto[] = ['Sem cobrança', 'Sem injeção', 'Cobrança agrupada'];

export interface DadosStatusBoleto {
  status_boleto_vigor?: string | null;
  status_fatura?: string | null;
  valor_boleto_vigor?: number | string | null;
  vencimento_boleto?: string | null;
  data_vencimento?: string | null;
  data_pgto_boleto?: string | null;
  quitado_em?: string | null;
  /** Declarado porque as telas selecionam a coluna, mas **deliberadamente não
   *  usado** na derivação: é resíduo do boleto anterior. Ver o cabeçalho. */
  cancelado_em?: string | null;
  energia_injetada_gd1_kwh?: number | string | null;
  energia_injetada_gd2_kwh?: number | string | null;
  /** Valor real da cobrança quando a fatura é de boleto único / empresa
   *  parceira — nesse caminho `valor_boleto_vigor` fica 0. Ver `valorDevido`. */
  valor_emissao_boleto_unico?: number | string | null;
  /** Título registrado no banco. Os 10 últimos dígitos são o valor cobrado de fato. */
  linha_digitavel_boleto?: string | null;
  /** Papel no boleto agrupado (#6284), derivado por gatilho no banco: 'membro' é a
   *  fatura de UC de agrupamento sem boleto próprio; 'boleto' é o boleto agrupado. */
  cobranca_agrupada?: string | null;
}

/**
 * Quanto a Vigor está cobrando nesta fatura.
 *
 * Em boleto único (e em toda fatura de empresa parceira) o valor cobrado NÃO
 * está em `valor_boleto_vigor` — esse campo fica 0 e o valor real, já somado
 * (Vigor/parceira + Equatorial), vai para `valor_emissao_boleto_unico`. É a
 * mesma precedência usada por quem de fato registra a cobrança no gateway:
 * `asaas-cobranca/index.ts:216` e `gerar-boleto-gran/desenhaPaginaGran.ts:243`.
 *
 * Enquanto o guard de valor olhava só `valor_boleto_vigor`, essas faturas eram
 * classificadas como 'Sem cobrança'/'Sem injeção' e sumiam da Central de
 * Inadimplência e do Painel Executivo: R$ 33.554,12 em 15 faturas vencidas
 * (medido em 04/09/2026) — dívida real, com boleto emitido e linha digitável.
 */
export function valorDevido(f: DadosStatusBoleto): number {
  const unico = Number(f.valor_emissao_boleto_unico);
  if (Number.isFinite(unico) && unico > 0) return unico;
  return Number(f.valor_boleto_vigor) || 0;
}

/**
 * Quanto o boleto emitido cobra de fato: o valor gravado na linha digitável, que é o
 * título registrado no banco. Sem linha válida (boleto ainda não emitido, ou linha de
 * arrecadação, que começa com 8), vale `valorDevido`.
 *
 * No boleto único o título soma Vigor + Equatorial. Comparar o valor pago com
 * `valor_boleto_vigor` (só a parte da Vigor) mostrava a conta da Equatorial como juros:
 * chamado #6302, Eleon (OUT/2026), que pagou R$ 303,72 adiantado e a tela de Faturas
 * mostrou "Boleto R$ 258,76 · c/ juros". O valor pedido (`valor_emissao_boleto_unico`)
 * também não basta: antes da correção do n8n de 14/10/2026 o Sicoob registrava só a
 * Vigor, mesmo com o pedido somado.
 *
 * Valor 9999999999 é a linha falsa que o sandbox do Sicoob devolve (faturas
 * "Consumidor Teste"): não é cobrança, vale `valorDevido`.
 */
export function valorCobradoBoleto(f: DadosStatusBoleto): number {
  const d = String(f.linha_digitavel_boleto ?? '').replace(/\D/g, '');
  if (d.length === 47 && d[0] !== '8' && d.slice(37) !== '9999999999') {
    const v = Number(d.slice(37)) / 100;
    if (v > 0) return v;
  }
  return valorDevido(f);
}

/**
 * `true` só quando os dois campos de energia chegaram e a soma é zero — `false`
 * também quando o dado ainda não chegou (`null`/`undefined` nos dois), porque
 * não rotulamos como "sem injeção" um dado que simplesmente não temos ainda.
 */
function semInjecaoConfirmada(f: DadosStatusBoleto): boolean {
  const { energia_injetada_gd1_kwh, energia_injetada_gd2_kwh } = f;
  if (energia_injetada_gd1_kwh == null && energia_injetada_gd2_kwh == null) return false;
  const gd1 = Number(energia_injetada_gd1_kwh) || 0;
  const gd2 = Number(energia_injetada_gd2_kwh) || 0;
  return gd1 + gd2 === 0;
}

/**
 * Chave de agrupamento de cliente, para os dois painéis consolidarem igual.
 *
 * É o nome normalizado, **não** o CPF/CNPJ: 331 das faturas vencidas em
 * 12/08/2026 estão sem `cliente_cpf_cnpj`, então uma chave `cpf || nome`
 * quebra o mesmo cliente em dois grupos — era por isso que SUPER GELO e
 * ROBSON ADRIANO CUNHA apareciam duas vezes no Top 10 Devedores e a contagem
 * de "clientes inadimplentes" dava 382 no painel contra 320 na inadimplência.
 */
export function chaveCliente(nome: string | null | undefined): string {
  return (nome || 'Desconhecido').trim().toUpperCase();
}

function paraDataLocalSemHora(valor: string): Date {
  const [ano, mes, dia] = valor.split('T')[0].split('-').map(Number);
  return new Date(ano, (mes || 1) - 1, dia || 1);
}

export function derivarStatusBoleto(f: DadosStatusBoleto, hoje: Date = new Date()): StatusBoleto {
  // Fatura que ainda não chegou a ter boleto calculado não é cobrança. É daqui
  // que vinha o caso "Normanha": R$ 23.901,33 em 'Aguardando processamento',
  // sem boleto emitido, contado como vencido só porque a data passou.
  // Ausência de `status_fatura` não gate: preferimos mostrar a mais esconder.
  if (f.status_fatura && !STATUS_FATURA_COM_BOLETO.includes(f.status_fatura as any)) {
    return 'Sem cobrança';
  }

  const cru = (f.status_boleto_vigor || '').trim().toLowerCase();

  // Só o campo. `cancelado_em` fica de fora de propósito — ver o cabeçalho.
  if (cru === 'cancelado') return 'Cancelado';
  if (cru === 'quitado' || f.quitado_em) return 'Quitado';
  if (cru === 'pago' || f.data_pgto_boleto) return 'Pago';
  if (cru === 'parcelado') return 'Parcelado';

  // Boleto sem valor não é dívida. São ~590 faturas de saldo zero, sem número
  // de cobrança e sem linha digitável, que inflavam a CONTAGEM de vencidos
  // (não o valor) e por tabela a de clientes inadimplentes.
  //
  // Dentro desse grupo, distinguir "Sem injeção" (a UC não recebeu compensação
  // SCEE neste ciclo — cenário normal do modelo, útil separar em auditoria/
  // relatório gerencial) do "Sem cobrança" genérico (outros motivos: parcelado
  // à parte, cancelado antes de calcular, etc). Só o gate de valor entra nessa
  // distinção — o gate de pipeline (`status_fatura` fora de
  // STATUS_FATURA_COM_BOLETO, acima) continua sempre 'Sem cobrança': naquele
  // ponto a fatura pode nem ter passado pelo robô de extração ainda, então
  // auditar "teve injeção ou não" não faz sentido.
  if (!(valorDevido(f) > 0)) {
    return semInjecaoConfirmada(f) ? 'Sem injeção' : 'Sem cobrança';
  }

  // Fatura de UC de agrupamento (Prefeitura de Morrinhos) não é cobrança: quem
  // cobra é o boleto agrupado da Secretaria, com vencimento próprio (dia 15 do mês
  // seguinte à emissão). A data aqui é a da conta da Equatorial, copiada no
  // cálculo — por ela, 77 faturas de SET/2026 (R$ 70.494,69) virariam "Vencido"
  // em 31/10, duas semanas antes do boleto real, e a dívida contaria duas vezes
  // depois do dia 15 (#6284). Pago/Quitado acima continuam valendo: quando o
  // boleto agrupado é pago, o fechamento quita as faturas das UCs.
  if (f.cobranca_agrupada === 'membro') return 'Cobrança agrupada';

  const vencimentoStr = f.vencimento_boleto || f.data_vencimento;
  if (vencimentoStr) {
    const vencimento = paraDataLocalSemHora(vencimentoStr);
    const hojeSemHora = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
    if (vencimento.getTime() < hojeSemHora.getTime()) return 'Vencido';
  }
  return 'Em aberto';
}
